import React, { useEffect, useState } from 'react';
import {
  CustomersQuery,
  RepairsOrderClient,
  RepairsOrderQuery,
} from 'services/api/api-client.ts';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import {
  Button,
  ButtonColor,
} from '../../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import styles from '../FormStyles.module.scss';
import clsx from 'clsx';
import { useWatch } from 'react-hook-form';
import { Checkbox } from '../../../../components/uikit/inputs/Checkbox.tsx';

type DeliveryForm = {
  contact: string;
  phoneNumber: string;
  street: string;
  postCode: string;
  city: string;
  countryCode: string;
  contactId: string;
};

type CustomerInfoFormProps = {
  onNextStep: () => void;
};

// enum DeliveryAddressStep {
//   UseDifferentAddress = 'UseDifferentAddress',
//   SameAsCustomerInfo = 'SameAsCustomerInfo',
//   FillContactID = 'FillContactID',
// }

export const CustomerInfoForm = ({ onNextStep }: CustomerInfoFormProps) => {
  const i18n = useScopedTranslation('Page.CreateRepairRequest');

  // all commented code is for future use, when we will add organisations and lm-identity

  // const [deliveryAddressStep, setDeliveryAddressStep] =
  //   useState<DeliveryAddressStep>(DeliveryAddressStep.UseDifferentAddress);
  const [isSameAsCustomer, setIsSameAsCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [hasContactIdSearchResult, setHasContactIdSearchResult] =
  //   useState(false);
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState('');

  // const isUseDifferentAddressEnabled =
  //   deliveryAddressStep === DeliveryAddressStep.UseDifferentAddress;
  // const isSameAsCustomerInfoEnabled =
  //   deliveryAddressStep === DeliveryAddressStep.SameAsCustomerInfo;
  // const isFillContactIDEnabled =
  //   deliveryAddressStep === DeliveryAddressStep.FillContactID;

  const deliveryAddressForm = useAdvancedForm<DeliveryForm>(async () => {}, {
    defaultValues: {
      contact: '',
      phoneNumber: '',
      street: '',
      postCode: '',
      city: '',
      countryCode: '',
      contactId: '',
    },
  });

  const { reset: resetDeliveryAddressForm } = deliveryAddressForm;

  const [contactId, city, street, countryCode, postCode, contact, phoneNumber] =
    useWatch({
      control: deliveryAddressForm.control,
      name: [
        'contactId',
        'city',
        'street',
        'countryCode',
        'postCode',
        'contact',
        'phoneNumber',
      ],
    });

  const customerQuery = CustomersQuery.useGetMyCustomerInfoQuery(
    i18n.i18n.language,
    {
      throwOnError: false,
      staleTime: 60 * 1000,
    },
  );

  const customerInfo = customerQuery?.data;
  const phoneNumberFromRequest = customerInfo?.phoneNumber;

  useEffect(() => {
    if (phoneNumberFromRequest) {
      setCustomerPhoneNumber(phoneNumberFromRequest);
    }
  }, [phoneNumberFromRequest]);

  const internalRepairsInvoiceAddressQuery =
    RepairsOrderQuery.useGetInternalRepairOrderInvoiceAddressQuery({
      throwOnError: false,
      staleTime: 60 * 1000,
      enabled: !isSameAsCustomer,
    });

  const internalRepairInfo = internalRepairsInvoiceAddressQuery.data;
  const internalContactId = internalRepairInfo?.contactId;

  useEffect(() => {
    if (!isSameAsCustomer && internalRepairInfo) {
      resetDeliveryAddressForm({
        contact: internalRepairInfo.contact,
        contactId: internalContactId,
        countryCode: internalRepairInfo.countryCode,
        city: internalRepairInfo.city,
        street: internalRepairInfo.street,
        postCode: internalRepairInfo.postCode,
        phoneNumber: internalRepairInfo.phoneNumber,
      });
    }
  }, [isSameAsCustomer, internalContactId]);

  const handleSameAsCustomerChange = () => {
    // setHasContactIdSearchResult(false);
    if (customerInfo) {
      resetDeliveryAddressForm({
        contact: customerInfo.companyFullName,
        contactId: customerInfo.customerNumber,
        countryCode: customerInfo.countryCode,
        city: customerInfo.city,
        street: customerInfo.street,
        postCode: customerInfo.postCode,
        phoneNumber: customerPhoneNumber,
      });
    }
  };

  // const handleSearchByContactId = async () => {
  //   const trimmedContactId = contactId?.trim();
  //
  //   if (!trimmedContactId) {
  //     return;
  //   }
  //
  //   try {
  //     setIsLoading(true);
  //     const d = await RepairsOrderClient.getInvoiceAddress(trimmedContactId);
  //
  //     resetDeliveryAddressForm({
  //       contact: d.contact,
  //       contactId: d.contactId,
  //       countryCode: d.countryCode,
  //       city: d.city,
  //       street: d.street,
  //       postCode: d.postCode,
  //       phoneNumber: d.phoneNumber,
  //     });
  //
  //     deliveryAddressForm.clearErrors('contactId');
  //     setHasContactIdSearchResult(true);
  //   } catch (e) {
  //     deliveryAddressForm.setError('contactId', {
  //       message: (e as Error)?.message || i18n.t('contact_id_not_found'),
  //     });
  //     setHasContactIdSearchResult(false);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  useEffect(() => {
    if (isSameAsCustomer) {
      deliveryAddressForm.setValue('phoneNumber', customerPhoneNumber);
    }
  }, [customerPhoneNumber]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      await RepairsOrderClient.createInternalRepairOrder({
        contactId,
        city,
        street,
        countryCode,
        postCode,
        contact,
        phoneNumber,
      });
      onNextStep();
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Loading
      loading={
        internalRepairsInvoiceAddressQuery.isLoading ||
        customerQuery.isLoading ||
        isLoading
      }
    >
      <div className={styles.sectionHeader}>
        <div className={styles.formTitle}>{i18n.t('form_title')}</div>
        <div className={styles.sectionSubtitle}> {i18n.t('step1')}</div>
      </div>
      {/* Customer info group */}
      <div className={styles.sectionTitle}>{i18n.t('customer_info')}</div>

      <div>
        <div className={clsx(styles.columnContainer, styles.rowGap20)}>
          <div className={clsx(styles.columnContainer, styles.rowGap6)}>
            <div className={styles.deviceGreyText}>
              {i18n.t('company_full_name')}
            </div>
            <div className={styles.text}>
              {customerInfo?.companyFullName || '-'}
            </div>
          </div>
          <div className={clsx(styles.rowContainer, styles.columnGap18)}>
            <div className={styles.cell}>
              <div className={styles.deviceGreyText}>{i18n.t('street')}</div>
              <div className={styles.text}>{customerInfo?.street || '-'}</div>
            </div>
            <div className={styles.cell}>
              <div className={styles.deviceGreyText}>{i18n.t('city')}</div>
              <div className={styles.text}>{customerInfo?.city || '-'}</div>
            </div>
          </div>
          <div className={clsx(styles.rowContainer, styles.columnGap18)}>
            <div className={styles.cell}>
              <div className={styles.deviceGreyText}>{i18n.t('country')}</div>
              <div className={styles.text}>
                {customerInfo?.countryName || '-'}
              </div>
            </div>
            <div className={styles.cell}>
              <div className={styles.deviceGreyText}>
                {i18n.t('country_code')}
              </div>
              <div className={styles.text}>
                {customerInfo?.countryCode || '-'}
              </div>
            </div>
          </div>
          <div className={clsx(styles.rowContainer, styles.columnGap18)}>
            <div className={styles.cell}>
              <div className={styles.deviceGreyText}>{i18n.t('post_code')}</div>
              <div className={styles.text}>{customerInfo?.postCode || '-'}</div>
            </div>
            <div className={styles.cell}>
              <div className={styles.deviceGreyText}>{i18n.t('email')}</div>
              <div className={styles.text}>{customerInfo?.email || '-'}</div>
            </div>
          </div>
        </div>
        <Input
          value={customerPhoneNumber}
          onChange={(e) => setCustomerPhoneNumber(e.target.value)}
          containerClassName={clsx(styles.cell, styles.marginTop44)}
          className={styles.width328}
          topHelperText={i18n.t('phone_number')}
          placeholder={i18n.t('phone_number')}
        />
      </div>

      <div className={styles.divider} />

      {/* Delivery address group */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>{i18n.t('delivery_address')}</div>
      </div>
      <div className={clsx(styles.rowContainer)}>
        <Checkbox
          actionText={i18n.t('same_as_customer_info')}
          isChecked={isSameAsCustomer}
          onChange={(e) => {
            setIsSameAsCustomer(e.target.checked);
            handleSameAsCustomerChange();
          }}
        />
        {/*<RadioButton*/}
        {/*  name={'delivery-address'}*/}
        {/*  title={i18n.t('fill_contact_ID')}*/}
        {/*  className={styles.flex1}*/}
        {/*  value={DeliveryAddressStep.FillContactID}*/}
        {/*  checked={isFillContactIDEnabled}*/}
        {/*  onChange={() => {*/}
        {/*    deliveryAddressForm.setValue('contactId', '');*/}
        {/*    setDeliveryAddressStep(DeliveryAddressStep.FillContactID);*/}
        {/*  }}*/}
        {/*/>*/}
      </div>
      {!isSameAsCustomer &&
        !internalRepairsInvoiceAddressQuery.isLoading &&
        !customerQuery.isLoading && (
          <div className={styles.fields}>
            <Input
              {...deliveryAddressForm.register('contact')}
              containerClassName={styles.cell}
              topHelperText={i18n.t('contact')}
              placeholder={i18n.t('contact')}
              fullWidth
            />
            <div className={clsx(styles.rowContainer, styles.columnGap18)}>
              <Input
                {...deliveryAddressForm.register('street')}
                containerClassName={styles.cell}
                topHelperText={i18n.t('street')}
                placeholder={i18n.t('street')}
                fullWidth
              />
              <Input
                {...deliveryAddressForm.register('city')}
                containerClassName={styles.cell}
                topHelperText={i18n.t('city')}
                placeholder={i18n.t('city')}
                fullWidth
              />
            </div>
            <div className={clsx(styles.rowContainer, styles.columnGap18)}>
              <Input
                {...deliveryAddressForm.register('countryCode')}
                containerClassName={styles.cell}
                topHelperText={i18n.t('country_code')}
                placeholder={i18n.t('country_code')}
                fullWidth
              />
              <Input
                {...deliveryAddressForm.register('postCode')}
                containerClassName={styles.cell}
                topHelperText={i18n.t('post_code')}
                placeholder={i18n.t('post_code')}
                fullWidth
              />
              <Input
                {...deliveryAddressForm.register('phoneNumber')}
                containerClassName={styles.cell}
                topHelperText={i18n.t('phone_number')}
                placeholder={i18n.t('phone_number')}
                fullWidth
              />
            </div>
          </div>
        )}
      {/*{isFillContactIDEnabled && (*/}
      {/*  <div className={styles.contactIdRow}>*/}
      {/*    <Input*/}
      {/*      {...deliveryAddressForm.register('contactId')}*/}
      {/*      topHelperText={i18n.t('contact_id')}*/}
      {/*      placeholder={i18n.t('contact_id_placeholder')}*/}
      {/*      errorText={deliveryAddressForm.formState.errors.contactId?.message}*/}
      {/*      className={styles.width328}*/}
      {/*    />*/}
      {/*    <ActionText*/}
      {/*      text={i18n.t('search')}*/}
      {/*      onClickHandler={handleSearchByContactId}*/}
      {/*      disabled={!contactId?.trim()}*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*)}*/}
      {/*{isFillContactIDEnabled && hasContactIdSearchResult && !isLoading && (*/}
      {/*  <div className={clsx(styles.columnContainer, styles.rowGap20)}>*/}
      {/*    <div className={clsx(styles.cell, styles.marginTop8)}>*/}
      {/*      <div className={styles.deviceGreyText}>{i18n.t('contact')}</div>*/}
      {/*      <div className={styles.text}>{contact || '-'}</div>*/}
      {/*    </div>*/}
      {/*    <div*/}
      {/*      className={clsx(*/}
      {/*        styles.rowContainer,*/}
      {/*        styles.columnGap18,*/}
      {/*        styles.rowGap20,*/}
      {/*      )}*/}
      {/*    >*/}
      {/*      <div className={styles.cell}>*/}
      {/*        <div className={styles.deviceGreyText}>{i18n.t('street')}</div>*/}
      {/*        <div className={styles.text}>{street || '-'}</div>*/}
      {/*      </div>*/}
      {/*      <div className={styles.cell}>*/}
      {/*        <div className={styles.deviceGreyText}>{i18n.t('city')}</div>*/}
      {/*        <div className={styles.text}>{city || '-'}</div>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*    <div className={clsx(styles.rowContainer, styles.columnGap18)}>*/}
      {/*      <div className={styles.cell}>*/}
      {/*        <div className={styles.deviceGreyText}>*/}
      {/*          {i18n.t('country_code')}*/}
      {/*        </div>*/}
      {/*        <div className={styles.text}>{countryCode || '-'}</div>*/}
      {/*      </div>*/}
      {/*      <div className={styles.cell}>*/}
      {/*        <div className={styles.deviceGreyText}>{i18n.t('post_code')}</div>*/}
      {/*        <div className={styles.text}>{postCode || '-'}</div>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*    <Input*/}
      {/*      {...deliveryAddressForm.register('phoneNumber')}*/}
      {/*      containerClassName={clsx(styles.cell, styles.marginTop26)}*/}
      {/*      topHelperText={i18n.t('phone_number')}*/}
      {/*      placeholder={i18n.t('phone_number')}*/}
      {/*      fullWidth*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*)}*/}
      <div className={styles.footer}>
        <Button
          title={i18n.t('next_step')}
          color={ButtonColor.Primary}
          className={styles.submit}
          onClick={onSubmit}
          testId={'create-repair-request-submit'}
          // disabled={!hasContactIdSearchResult && isFillContactIDEnabled}
        />
      </div>
    </Loading>
  );
};
